import { Fragment, useMemo, useState } from "react";
import { faUpRightAndDownLeftFromCenter, faX } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import dayjs from "global/dayjs";
import { QueryResultModel } from "services/api/cplapi/CPLAPI";
import "app/modules/aiAssistant/styles/shared.scss";
import "./ResultsTable.scss";
import {
  compareValues,
  columnType,
  formatCell,
  isNumeric,
  toCsv,
} from "app/modules/aiAssistant/conversation/messages/components/resultsTable/resultsTableHelpers";
import ResultsGrid, {
  ResultsSort,
} from "app/modules/aiAssistant/conversation/messages/components/resultsTable/components/ResultsGrid";

type Props = { payload: QueryResultModel | undefined; testIdKey: string };

const INLINE_PAGE_SIZE = 25;
const FULLSCREEN_PAGE_SIZE = 50;

const ResultsTable = ({ payload, testIdKey }: Props) => {
  const [sort, setSort] = useState<ResultsSort>(null);
  const [showFullscreen, setShowFullscreen] = useState(false);

  const columns = payload?.columns;
  const rows = payload?.rows;

  const sortedRows = useMemo(() => {
    if (!rows || !columns || sort == null) {
      return rows;
    }

    const typeName = columnType(columns[sort.index]);

    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = compareValues(a[sort.index], b[sort.index], typeName);
      return sort.direction === "asc" ? cmp : -cmp;
    });

    return copy;
  }, [rows, columns, sort]);

  const formattedRows = useMemo(() => {
    if (!sortedRows || !columns) {
      return [];
    }

    return sortedRows.map((row) => row.map((cell, ci) => formatCell(cell, columnType(columns[ci]))));
  }, [sortedRows, columns]);

  if (columns == undefined || rows == undefined) return <></>;

  // A single row reads better as a list of names and values
  if (rows.length === 1 && columns.length > 1) {
    return (
      <dl className="assistimo-result-detail">
        {columns.map((c, i) => (
          <Fragment key={i}>
            <dt className="assistimo-result-detail__name">{c.name}</dt>
            <dd
              className={`assistimo-result-detail__value ${
                isNumeric(columnType(c)) ? "assistimo-result-detail__value--number" : ""
              }`}
            >
              {formatCell(rows[0][i], columnType(c))}
            </dd>
          </Fragment>
        ))}
      </dl>
    );
  }

  const cycleSort = (index: number) => {
    setSort((prev) => {
      if (prev == null || prev.index !== index) {
        return { index, direction: "asc" };
      }

      if (prev.direction === "asc") {
        return { index, direction: "desc" };
      }

      return null;
    });
  };

  const exportCsv = () => {
    // The byte order mark lets Excel read the file as UTF-8
    const byteOrderMark = String.fromCharCode(0xfeff);
    const blob = new Blob([byteOrderMark, toCsv(columns, sortedRows ?? rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `assistimo-results-${dayjs().format("YYYY-MM-DD-HHmm")}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url));
  };

  // A new sort starts both tables back on the first page
  const gridKey = sort ? `${sort.index}-${sort.direction}` : "unsorted";
  const gridProps = {
    columns,
    formattedRows,
    sort,
    onSort: cycleSort,
    onExportCsv: exportCsv,
    isTruncated: payload?.isTruncated,
  };

  return (
    <>
      <ResultsGrid
        key={gridKey}
        {...gridProps}
        pageSize={INLINE_PAGE_SIZE}
        testIds={inlineTestIds(testIdKey)}
        headerActions={
          <button
            data-test-id={`assistimo-results-table__expand-button-${testIdKey}`}
            type="button"
            className="assistimo-results__action"
            onClick={() => setShowFullscreen(true)}
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
            Expand
          </button>
        }
      />
      <Modal
        show={showFullscreen}
        onHide={() => setShowFullscreen(false)}
        dialogClassName="assistimo-results-dialog"
        backdrop="static"
        keyboard
      >
        <Modal.Header>
          <Modal.Title>Results</Modal.Title>
          <button
            data-test-id={`assistimo-results-modal__close-button-${testIdKey}`}
            type="button"
            className="assistimo-icon-button"
            aria-label="Close"
            onClick={() => setShowFullscreen(false)}
          >
            <FontAwesomeIcon icon={faX} />
          </button>
        </Modal.Header>
        <Modal.Body>
          <ResultsGrid
            key={gridKey}
            {...gridProps}
            pageSize={FULLSCREEN_PAGE_SIZE}
            testIds={modalTestIds(testIdKey)}
            isFullscreen
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

// The table repeats once per result, so each id ends with the result key
const inlineTestIds = (key: string) => ({
  exportCsvButton: `assistimo-results-table__export-csv-button-${key}`,
  sortColumnButton: (colIndex: number) => `assistimo-results-table__sort-column-button-${colIndex}-${key}`,
  previousPageButton: `assistimo-results-table__previous-page-button-${key}`,
  nextPageButton: `assistimo-results-table__next-page-button-${key}`,
});

const modalTestIds = (key: string) => ({
  exportCsvButton: `assistimo-results-modal__export-csv-button-${key}`,
  sortColumnButton: (colIndex: number) => `assistimo-results-modal__sort-column-button-${colIndex}-${key}`,
  previousPageButton: `assistimo-results-modal__previous-page-button-${key}`,
  nextPageButton: `assistimo-results-modal__next-page-button-${key}`,
});

export default ResultsTable;
